import React, {
    useState
} from "react";

import {
    submitPartnerOnboarding
} from "../services/DeliveryPartnerService";


const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #dedede",
    borderRadius: 12,
    padding: "13px 14px",
    fontSize: 14,
    outline: "none",
    background: "#fff"
};


const labelStyle = {
    display: "block",
    marginBottom: 7,
    fontSize: 13,
    fontWeight: 700,
    color: "#333"
};


const buttonStyle = {
    border: 0,
    borderRadius: 12,
    padding: "13px 18px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    background: "#111",
    color: "#fff"
};


const secondaryButtonStyle = {
    border: "1px solid #ddd",
    borderRadius: 12,
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    background: "#fff",
    color: "#111"
};


const cardStyle = {
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: 20,
    padding: 24,
    boxSizing: "border-box"
};


const STEPS = [
    {
        id: "personal",
        label: "Personal"
    },
    {
        id: "area",
        label: "Area"
    },
    {
        id: "mode",
        label: "Delivery mode"
    },
    {
        id: "vehicle",
        label: "Vehicle"
    },
    {
        id: "documents",
        label: "Documents"
    },
    {
        id: "emergency",
        label: "Emergency"
    }
];


function FileField({
    label,
    name,
    value,
    onChange,
    required = false,
    accept
}) {
    return (
        <div>
            <label style={labelStyle}>
                {label}
                {required && (
                    <span
                        style={{
                            color: "#b42318",
                            marginLeft: 3
                        }}
                    >
                        *
                    </span>
                )}
            </label>

            <input
                type="file"
                accept={accept}
                onChange={event => {
                    onChange(
                        name,
                        event.target.files?.[0] || null
                    );
                }}
                style={{
                    ...inputStyle,
                    padding: "10px 12px"
                }}
            />

            {value && (
                <div
                    style={{
                        marginTop: 7,
                        fontSize: 12,
                        color: "#555"
                    }}
                >
                    Selected: {value.name}
                </div>
            )}
        </div>
    );
}


export default function DeliveryPartnerOnboarding({
    onComplete
}) {

    const [stepIndex, setStepIndex] =
        useState(0);

    const [submitting, setSubmitting] =
        useState(false);

    const [error, setError] =
        useState("");

    const [success, setSuccess] =
        useState(false);


    const [form, setForm] =
        useState({

            fullName: "",
            phone: "",
            dateOfBirth: "",

            address: "",
            city: "",
            state: "",
            area: "",

            deliveryMode: "",

            vehicleType: "",
            vehicleNumber: "",

            identityDocument: null,
            drivingLicense: null,
            vehicleRc: null,
            selfie: null,

            emergencyName: "",
            emergencyPhone: "",
            emergencyRelation: ""

        });


    const currentStep =
        STEPS[stepIndex];


    function updateField(
        field,
        value
    ) {

        setForm(current => ({
            ...current,
            [field]: value
        }));

        setError("");
    }


    function updateFile(
        field,
        value
    ) {

        setForm(current => ({
            ...current,
            [field]: value
        }));

        setError("");
    }


    function validateStep() {

        setError("");


        if (
            currentStep.id === "personal"
        ) {

            if (
                !form.fullName.trim() ||
                !form.phone.trim()
            ) {

                setError(
                    "Full name and phone number are required."
                );

                return false;
            }

        }


        if (
            currentStep.id === "area"
        ) {

            if (
                !form.address.trim() ||
                !form.city.trim() ||
                !form.state.trim() ||
                !form.area.trim()
            ) {

                setError(
                    "Address, city, state and area are required."
                );

                return false;
            }

        }


        if (
            currentStep.id === "mode"
        ) {

            if (
                !form.deliveryMode
            ) {

                setError(
                    "Please select a delivery mode."
                );

                return false;
            }

        }


        if (
            currentStep.id === "vehicle"
        ) {

            if (
                !form.vehicleType.trim() ||
                !form.vehicleNumber.trim()
            ) {

                setError(
                    "Vehicle type and vehicle number are required."
                );

                return false;
            }

        }


        if (
            currentStep.id === "documents"
        ) {

            if (
                !form.identityDocument ||
                !form.drivingLicense ||
                !form.vehicleRc ||
                !form.selfie
            ) {

                setError(
                    "Identity document, driving license, vehicle RC and selfie are required."
                );

                return false;
            }

        }


        if (
            currentStep.id === "emergency"
        ) {

            if (
                !form.emergencyName.trim() ||
                !form.emergencyPhone.trim() ||
                !form.emergencyRelation.trim()
            ) {

                setError(
                    "Emergency contact name, phone and relation are required."
                );

                return false;
            }

        }


        return true;
    }


    function nextStep() {

        if (!validateStep()) {
            return;
        }


        if (
            stepIndex <
            STEPS.length - 1
        ) {

            setStepIndex(
                current => current + 1
            );

        }

    }


    function previousStep() {

        setError("");

        if (
            stepIndex > 0
        ) {

            setStepIndex(
                current => current - 1
            );

        }

    }


    async function handleSubmit() {

        if (!validateStep()) {
            return;
        }


        setSubmitting(true);
        setError("");


        try {

            await submitPartnerOnboarding(
                form
            );


            setSuccess(true);


            if (
                typeof onComplete ===
                "function"
            ) {

                onComplete();

            }

        } catch (err) {

            console.error(
                "Partner onboarding error:",
                err
            );


            setError(
                err?.message ||
                "Unable to submit your partner application."
            );

        } finally {

            setSubmitting(false);

        }

    }


    if (success) {

        return (

            <div
                style={{
                    minHeight: "100%",
                    background: "#f7f7f7",
                    padding: 24,
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >

                <div
                    style={{
                        ...cardStyle,
                        maxWidth: 620,
                        width: "100%",
                        textAlign: "center"
                    }}
                >

                    <div
                        style={{
                            width: 62,
                            height: 62,
                            borderRadius: "50%",
                            background: "#effaf2",
                            color: "#18753c",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 18px",
                            fontSize: 30,
                            fontWeight: 800
                        }}
                    >
                        ?
                    </div>


                    <h1
                        style={{
                            margin: 0,
                            fontSize: 28,
                            fontWeight: 850,
                            color: "#111"
                        }}
                    >
                        Application submitted
                    </h1>


                    <p
                        style={{
                            marginTop: 12,
                            color: "#666",
                            fontSize: 15,
                            lineHeight: 1.6
                        }}
                    >
                        Your delivery partner application
                        has been submitted successfully.
                    </p>


                    <div
                        style={{
                            marginTop: 22,
                            padding: 16,
                            borderRadius: 14,
                            background: "#f7f7f7",
                            textAlign: "left"
                        }}
                    >

                        <div
                            style={{
                                fontSize: 12,
                                color: "#777",
                                marginBottom: 5
                            }}
                        >
                            Verification status
                        </div>

                        <strong
                            style={{
                                fontSize: 16
                            }}
                        >
                            Pending verification
                        </strong>

                        <div
                            style={{
                                marginTop: 8,
                                fontSize: 13,
                                color: "#777",
                                lineHeight: 1.5
                            }}
                        >
                            An Area Manager will review
                            your information and documents.
                            You will be able to go online
                            after your application is verified.
                        </div>

                    </div>

                </div>

            </div>

        );

    }


    return (

        <div
            style={{
                minHeight: "100%",
                background: "#f7f7f7",
                padding: "28px 20px 50px",
                boxSizing: "border-box"
            }}
        >

            <div
                style={{
                    maxWidth: 900,
                    margin: "0 auto"
                }}
            >

                <div
                    style={{
                        marginBottom: 28
                    }}
                >

                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 800,
                            color: "#777",
                            letterSpacing: 1
                        }}
                    >
                        myFAVV DELIVERY
                    </div>


                    <h1
                        style={{
                            margin: "7px 0 0",
                            fontSize: 32,
                            lineHeight: 1.15,
                            fontWeight: 850,
                            color: "#111"
                        }}
                    >
                        Become a Delivery Partner
                    </h1>


                    <p
                        style={{
                            margin: "9px 0 0",
                            color: "#777",
                            fontSize: 14,
                            lineHeight: 1.5
                        }}
                    >
                        Complete your profile and submit
                        your documents for verification.
                    </p>

                </div>


                {/* Progress */}

                <div
                    style={{
                        ...cardStyle,
                        marginBottom: 18,
                        padding: "18px 20px"
                    }}
                >

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                `repeat(${STEPS.length}, minmax(0, 1fr))`,
                            gap: 8
                        }}
                    >

                        {STEPS.map(
                            (step, index) => {

                                const completed =
                                    index <
                                    stepIndex;

                                const active =
                                    index ===
                                    stepIndex;


                                return (

                                    <div
                                        key={step.id}
                                        style={{
                                            minWidth: 0
                                        }}
                                    >

                                        <div
                                            style={{
                                                height: 5,
                                                borderRadius: 10,
                                                background:
                                                    completed ||
                                                    active
                                                        ? "#111"
                                                        : "#e8e8e8"
                                            }}
                                        />


                                        <div
                                            style={{
                                                marginTop: 7,
                                                fontSize: 11,
                                                fontWeight:
                                                    active ||
                                                    completed
                                                        ? 800
                                                        : 600,
                                                color:
                                                    active ||
                                                    completed
                                                        ? "#111"
                                                        : "#999",
                                                whiteSpace:
                                                    "nowrap",
                                                overflow:
                                                    "hidden",
                                                textOverflow:
                                                    "ellipsis"
                                            }}
                                        >
                                            {step.label}
                                        </div>

                                    </div>

                                );

                            }
                        )}

                    </div>

                </div>


                {error && (

                    <div
                        style={{
                            marginBottom: 18,
                            padding: 13,
                            borderRadius: 12,
                            background: "#fff1f1",
                            color: "#b42318",
                            fontSize: 14,
                            fontWeight: 600
                        }}
                    >
                        {error}
                    </div>

                )}


                <div
                    style={cardStyle}
                >

                    {/* PERSONAL */}

                    {currentStep.id ===
                        "personal" && (

                        <>

                            <StepHeading
                                title="Personal information"
                                text="Tell us about yourself."
                            />


                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(250px, 1fr))",
                                    gap: 18
                                }}
                            >

                                <Field
                                    label="Full name"
                                    value={form.fullName}
                                    onChange={value =>
                                        updateField(
                                            "fullName",
                                            value
                                        )
                                    }
                                    placeholder="Rahul Das"
                                    required
                                />


                                <Field
                                    label="Phone number"
                                    value={form.phone}
                                    onChange={value =>
                                        updateField(
                                            "phone",
                                            value
                                        )
                                    }
                                    placeholder="+91 98765 43210"
                                    required
                                />


                                <Field
                                    label="Date of birth"
                                    type="date"
                                    value={form.dateOfBirth}
                                    onChange={value =>
                                        updateField(
                                            "dateOfBirth",
                                            value
                                        )
                                    }
                                />

                            </div>

                        </>

                    )}


                    {/* AREA */}

                    {currentStep.id ===
                        "area" && (

                        <>

                            <StepHeading
                                title="Your area"
                                text="Tell us where you will primarily operate."
                            />


                            <div
                                style={{
                                    display: "grid",
                                    gap: 18
                                }}
                            >

                                <Field
                                    label="Address"
                                    value={form.address}
                                    onChange={value =>
                                        updateField(
                                            "address",
                                            value
                                        )
                                    }
                                    placeholder="123 Example Street"
                                    required
                                />


                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fit, minmax(200px, 1fr))",
                                        gap: 18
                                    }}
                                >

                                    <Field
                                        label="Area"
                                        value={form.area}
                                        onChange={value =>
                                            updateField(
                                                "area",
                                                value
                                            )
                                        }
                                        placeholder="Salt Lake"
                                        required
                                    />


                                    <Field
                                        label="City"
                                        value={form.city}
                                        onChange={value =>
                                            updateField(
                                                "city",
                                                value
                                            )
                                        }
                                        placeholder="Kolkata"
                                        required
                                    />


                                    <Field
                                        label="State"
                                        value={form.state}
                                        onChange={value =>
                                            updateField(
                                                "state",
                                                value
                                            )
                                        }
                                        placeholder="West Bengal"
                                        required
                                    />

                                </div>

                            </div>

                        </>

                    )}


                    {/* MODE */}

                    {currentStep.id ===
                        "mode" && (

                        <>

                            <StepHeading
                                title="Delivery mode"
                                text="Choose the type of delivery vehicle you will use."
                            />


                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(180px, 1fr))",
                                    gap: 14
                                }}
                            >

                                {[
                                    [
                                        "bike",
                                        "??",
                                        "Bike"
                                    ],
                                    [
                                        "bicycle",
                                        "??",
                                        "Bicycle"
                                    ],
                                    [
                                        "car",
                                        "??",
                                        "Car"
                                    ],
                                    [
                                        "van",
                                        "??",
                                        "Van"
                                    ]
                                ].map(
                                    ([value, icon, label]) => {

                                        const selected =
                                            form.deliveryMode ===
                                            value;


                                        return (

                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() =>
                                                    updateField(
                                                        "deliveryMode",
                                                        value
                                                    )
                                                }
                                                style={{
                                                    border:
                                                        selected
                                                            ? "2px solid #111"
                                                            : "1px solid #ddd",
                                                    background:
                                                        selected
                                                            ? "#f5f5f5"
                                                            : "#fff",
                                                    borderRadius: 16,
                                                    padding: 22,
                                                    textAlign: "left",
                                                    cursor: "pointer"
                                                }}
                                            >

                                                <div
                                                    style={{
                                                        fontSize: 28,
                                                        marginBottom: 12
                                                    }}
                                                >
                                                    {icon}
                                                </div>


                                                <div
                                                    style={{
                                                        fontSize: 16,
                                                        fontWeight: 800,
                                                        color: "#111"
                                                    }}
                                                >
                                                    {label}
                                                </div>


                                                {selected && (

                                                    <div
                                                        style={{
                                                            marginTop: 7,
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            color: "#555"
                                                        }}
                                                    >
                                                        Selected
                                                    </div>

                                                )}

                                            </button>

                                        );

                                    }
                                )}

                            </div>

                        </>

                    )}


                    {/* VEHICLE */}

                    {currentStep.id ===
                        "vehicle" && (

                        <>

                            <StepHeading
                                title="Vehicle details"
                                text="Provide the vehicle information you will use for deliveries."
                            />


                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(250px, 1fr))",
                                    gap: 18
                                }}
                            >

                                <Field
                                    label="Vehicle type"
                                    value={form.vehicleType}
                                    onChange={value =>
                                        updateField(
                                            "vehicleType",
                                            value
                                        )
                                    }
                                    placeholder="Motorcycle"
                                    required
                                />


                                <Field
                                    label="Vehicle registration number"
                                    value={form.vehicleNumber}
                                    onChange={value =>
                                        updateField(
                                            "vehicleNumber",
                                            value
                                        )
                                    }
                                    placeholder="WB12AB1234"
                                    required
                                />

                            </div>

                        </>

                    )}


                    {/* DOCUMENTS */}

                    {currentStep.id ===
                        "documents" && (

                        <>

                            <StepHeading
                                title="Documents"
                                text="Upload clear copies of your verification documents."
                            />


                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(260px, 1fr))",
                                    gap: 20
                                }}
                            >

                                <FileField
                                    label="Identity document"
                                    name="identityDocument"
                                    value={
                                        form.identityDocument
                                    }
                                    onChange={
                                        updateFile
                                    }
                                    required
                                />


                                <FileField
                                    label="Driving license"
                                    name="drivingLicense"
                                    value={
                                        form.drivingLicense
                                    }
                                    onChange={
                                        updateFile
                                    }
                                    required
                                />


                                <FileField
                                    label="Vehicle RC"
                                    name="vehicleRc"
                                    value={
                                        form.vehicleRc
                                    }
                                    onChange={
                                        updateFile
                                    }
                                    required
                                />


                                <FileField
                                    label="Selfie"
                                    name="selfie"
                                    value={
                                        form.selfie
                                    }
                                    onChange={
                                        updateFile
                                    }
                                    required
                                />

                            </div>


                            <div
                                style={{
                                    marginTop: 20,
                                    padding: 14,
                                    borderRadius: 12,
                                    background: "#f7f7f7",
                                    color: "#666",
                                    fontSize: 12,
                                    lineHeight: 1.5
                                }}
                            >
                                Please upload clear, readable
                                documents. These files will be
                                stored securely with your delivery
                                partner application for manager
                                verification.
                            </div>

                        </>

                    )}


                    {/* EMERGENCY */}

                    {currentStep.id ===
                        "emergency" && (

                        <>

                            <StepHeading
                                title="Emergency contact"
                                text="Provide someone we can contact in case of an emergency."
                            />


                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(230px, 1fr))",
                                    gap: 18
                                }}
                            >

                                <Field
                                    label="Contact name"
                                    value={
                                        form.emergencyName
                                    }
                                    onChange={value =>
                                        updateField(
                                            "emergencyName",
                                            value
                                        )
                                    }
                                    placeholder="Amit Das"
                                    required
                                />


                                <Field
                                    label="Contact phone"
                                    value={
                                        form.emergencyPhone
                                    }
                                    onChange={value =>
                                        updateField(
                                            "emergencyPhone",
                                            value
                                        )
                                    }
                                    placeholder="+91 98765 12345"
                                    required
                                />


                                <Field
                                    label="Relationship"
                                    value={
                                        form.emergencyRelation
                                    }
                                    onChange={value =>
                                        updateField(
                                            "emergencyRelation",
                                            value
                                        )
                                    }
                                    placeholder="Brother"
                                    required
                                />

                            </div>


                            <div
                                style={{
                                    marginTop: 24,
                                    padding: 17,
                                    borderRadius: 14,
                                    background: "#f7f7f7"
                                }}
                            >

                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 800,
                                        marginBottom: 7
                                    }}
                                >
                                    Before you submit
                                </div>


                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "#666",
                                        lineHeight: 1.6
                                    }}
                                >
                                    Your application will be sent
                                    for manager verification.
                                    Your verification status will
                                    be set to <strong>pending</strong>.
                                    You cannot go online or accept
                                    delivery requests until an Area
                                    Manager verifies your application.
                                </div>

                            </div>

                        </>

                    )}


                    {/* ACTIONS */}

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 10,
                            flexWrap: "wrap",
                            marginTop: 30,
                            paddingTop: 20,
                            borderTop: "1px solid #eee"
                        }}
                    >

                        <div>

                            {stepIndex > 0 && (

                                <button
                                    type="button"
                                    style={
                                        secondaryButtonStyle
                                    }
                                    onClick={
                                        previousStep
                                    }
                                >
                                    Back
                                </button>

                            )}

                        </div>


                        <div
                            style={{
                                display: "flex",
                                gap: 10
                            }}
                        >

                            {stepIndex <
                                STEPS.length - 1 ? (

                                <button
                                    type="button"
                                    style={
                                        buttonStyle
                                    }
                                    onClick={
                                        nextStep
                                    }
                                >
                                    Continue
                                </button>

                            ) : (

                                <button
                                    type="button"
                                    style={{
                                        ...buttonStyle,
                                        opacity:
                                            submitting
                                                ? 0.65
                                                : 1
                                    }}
                                    onClick={
                                        handleSubmit
                                    }
                                    disabled={
                                        submitting
                                    }
                                >
                                    {submitting
                                        ? "Submitting..."
                                        : "Submit application"
                                    }
                                </button>

                            )}

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

}


function StepHeading({
    title,
    text
}) {

    return (

        <div
            style={{
                marginBottom: 24
            }}
        >

            <h2
                style={{
                    margin: 0,
                    fontSize: 22,
                    fontWeight: 850,
                    color: "#111"
                }}
            >
                {title}
            </h2>


            <div
                style={{
                    marginTop: 6,
                    fontSize: 14,
                    color: "#777"
                }}
            >
                {text}
            </div>

        </div>

    );

}


function Field({
    label,
    value,
    onChange,
    placeholder,
    type = "text",
    required = false
}) {

    return (

        <div>

            <label style={labelStyle}>

                {label}

                {required && (
                    <span
                        style={{
                            color: "#b42318",
                            marginLeft: 3
                        }}
                    >
                        *
                    </span>
                )}

            </label>


            <input
                type={type}
                value={value}
                onChange={event =>
                    onChange(
                        event.target.value
                    )
                }
                placeholder={placeholder}
                style={inputStyle}
            />

        </div>

    );

}